import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import SelectDropdown from "../../../ui/forms/SelectDropdown";
import { getLitigationClients, getLitigationBranches, getInvoiceBranches } from "../../../API/litigationApis";
import { showToast } from "../../../utils/handleToast";

const toBranchOption = (branch) => {
    if (typeof branch === "string" || typeof branch === "number") {
        const name = String(branch).trim();
        return name ? { label: name, value: name } : null;
    }

    if (!branch || typeof branch !== "object") return null;

    const nestedBranch = branch.branch || branch.branch_obj || null;
    const branchName =
        branch.branch_name ||
        branch.name ||
        nestedBranch?.branch_name ||
        nestedBranch?.name ||
        "";

    const name = String(branchName).trim();
    return name ? { label: name, value: name } : null;
};

function LigationBatchFilter({ show, close, onApplyFilters, currentFilters = {} }) {
    const [filters, setFilters] = useState({
        client: "",
        branch: "",
        customer_name: "",
        branch_name: "",
        lan: "",
        invoice_number: "",
        is_paid: "",
        invoice_date_from: "",
        invoice_date_to: "",
        ...currentFilters
    });
    const [clientOptions, setClientOptions] = useState([]);
    const [branchOptions, setBranchOptions] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [loadingBranches, setLoadingBranches] = useState(false);

    // Fetch clients when component mounts
    useEffect(() => {
        if (show) {
            fetchClients();
        }
    }, [show]);

    // Update local filters when currentFilters prop changes
    useEffect(() => {
        setFilters({
            client: "",
            branch: "",
            customer_name: "",
            branch_name: "",
            lan: "",
            invoice_number: "",
            is_paid: "",
            invoice_date_from: "",
            invoice_date_to: "",
            ...currentFilters
        });
        if (currentFilters?.client) {
            fetchBranches(currentFilters.client);
        }
    }, [currentFilters]);

    const fetchClients = async () => {
        setLoadingClients(true);
        try {
            const response = await getLitigationClients();
            const payload = response?.data || {};
            const rows = Array.isArray(payload?.data?.results)
                ? payload.data.results
                : Array.isArray(payload?.results)
                    ? payload.results
                    : [];

            const options = rows.map((client) => ({
                id: client.id,
                label: client.full_name || client.name || String(client.id),
                value: client.id,
            }));
            setClientOptions(options);
        } catch (error) {
            console.error("Error fetching clients:", error);
            showToast({ variant: "error", message: "Failed to fetch clients" });
        } finally {
            setLoadingClients(false);
        }
    };

    const fetchBranches = async (clientValue, customerName) => {
        if (!clientValue) {
            setBranchOptions([]);
            return;
        }
        setLoadingBranches(true);
        try {
            const selectedClient = clientOptions.find(
                (option) => option.value === clientValue || option.label === clientValue
            );
            const resolvedClientId = selectedClient?.id || selectedClient?.value;

            // Use invoice-specific branches endpoint with client_id
            const numericClientId = resolvedClientId || (String(clientValue).match(/^[0-9]+$/) ? Number(clientValue) : null);
            if (!numericClientId) {
                setBranchOptions([]);
                return;
            }

            const params = { client_id: numericClientId };
            if (customerName) params.customer_name = customerName;

            const res = await getInvoiceBranches(params);
            // Fallback to legacy branches endpoint if new one is not available
            const payload = res?.data || {};
            const results = Array.isArray(payload?.data?.results)
                ? payload.data.results
                : Array.isArray(payload?.results)
                    ? payload.results
                    : Array.isArray(payload?.data)
                        ? payload.data
                        : Array.isArray(payload)
                            ? payload
                            : [];

            if (Array.isArray(results) && results.length > 0) {
                // Map branch object to branch_name values for filtering
                const opts = results.map(toBranchOption).filter(Boolean);
                setBranchOptions(opts);
            } else {
                // Try legacy endpoint
                // legacy endpoint supports only client id
                if (!params.client_id) {
                    setBranchOptions([]);
                    return;
                }
                const legacy = await getLitigationBranches(params.client_id);
                if (legacy?.data?.results) {
                    const opts = legacy.data.results.map(toBranchOption).filter(Boolean);
                    setBranchOptions(opts);
                } else {
                    setBranchOptions([]);
                }
            }
        } catch (err) {
            console.error("Error fetching branches:", err);
            showToast({ variant: "error", message: "Failed to fetch branches" });
        } finally {
            setLoadingBranches(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        if (field === "client") {
            fetchBranches(value, filters.customer_name);
            setFilters(prev => ({ ...prev, branch: "", branch_name: "" }));
        }
        if (field === "customer_name") {
            // If customer name changes, refresh branches for selected client
            if (filters.client) {
                fetchBranches(filters.client, value);
            }
        }
    };

    const handleApplyFilters = () => {
        // Remove empty filters
        const cleanedFilters = {};
        Object.keys(filters).forEach(key => {
            const val = filters[key];
            if (val !== undefined && val !== null) {
                const str = typeof val === 'string' ? val.trim() : val;
                if (str !== "") cleanedFilters[key] = val;
            }
        });

        onApplyFilters(cleanedFilters);
        close();
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            client: "",
            branch: "",
            customer_name: "",
            branch_name: "",
            lan: "",
            invoice_number: "",
            is_paid: "",
            invoice_date_from: "",
            invoice_date_to: "",
        };
        setFilters(clearedFilters);
        setBranchOptions([]);
        onApplyFilters({});
        close();
    };

    const closeFilter = () => {
        close();
    };

    return (
        <Modal size="md" show={show} onHide={close} backdrop="static">
            <Modal.Header closeButton>
                    <Modal.Title>Filter Invoices</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row className="g-2">
                        <Col md={6}>
                            <Form.Group className="mb-0 mt-3">
                                <Form.Label className="small">Client</Form.Label>
                                <SelectDropdown
                                    options={[{ label: "All Clients", value: "" }, ...clientOptions]}
                                    placeholder={loadingClients ? "Loading..." : "Select client"}
                                    value={filters.client}
                                    onChange={(val) => handleFilterChange("client", val)}
                                    disabled={loadingClients}
                                    customStyles={{ control: { padding: "0.15rem 0", fontSize: "0.85rem" } }}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-0 mt-3">
                                <Form.Label className="small">Branch</Form.Label>
                                <SelectDropdown
                                    options={[{ label: "All Branches", value: "" }, ...branchOptions]}
                                    placeholder={loadingBranches ? "Loading..." : "Select branch"}
                                    value={filters.branch_name || filters.branch}
                                    onChange={(val) => handleFilterChange("branch_name", val)}
                                    disabled={loadingBranches || !filters.client}
                                    customStyles={{ control: { padding: "0.15rem 0", fontSize: "0.85rem" } }}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-0">
                                <Form.Label className="small">Customer Name</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    placeholder="Enter customer name"
                                    value={filters.customer_name}
                                    onChange={(e) => handleFilterChange("customer_name", e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-0">
                                <Form.Label className="small">LAN</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    placeholder="Enter LAN"
                                    value={filters.lan}
                                    onChange={(e) => handleFilterChange("lan", e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                       

                       

                        <Col md={6}>
                            <Form.Group className="mb-0">
                                <Form.Label className="small">Invoice Date From</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="date"
                                    value={filters.invoice_date_from}
                                    onChange={(e) => handleFilterChange("invoice_date_from", e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-0">
                                <Form.Label className="small">Invoice Date To</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="date"
                                    value={filters.invoice_date_to}
                                    onChange={(e) => handleFilterChange("invoice_date_to", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                         <Col md={6}>
                            <Form.Group className="mb-0">
                                <Form.Label className="small">Is Paid</Form.Label>
                                <SelectDropdown
                                    options={[{ label: "All", value: "" }, { label: "Paid", value: "true" }, { label: "Unpaid", value: "false" }]}
                                    placeholder="Status"
                                    value={String(filters.is_paid || "")}
                                    onChange={(val) => handleFilterChange("is_paid", val)}
                                    customStyles={{ control: { padding: "0.15rem 0", fontSize: "0.85rem" } }}
                                />
                            </Form.Group>
                        </Col>
                         <Col md={6}>
                            <Form.Group className="mb-0">
                                <Form.Label className="small">Invoice Number</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    placeholder="Enter invoice number"
                                    value={filters.invoice_number}
                                    onChange={(e) => handleFilterChange("invoice_number", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={handleClearFilters}>
                    Clear All
                </Button>
                <Button variant="secondary" onClick={closeFilter}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleApplyFilters}>
                    Apply Filters
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default LigationBatchFilter;

